if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      optionSelections = new Map();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        // Track each product option group independently so partial selections
        // cannot accidentally enable checkout or clear another group's state.
        this.initializeOptionSelectionState();
        requestAnimationFrame(() => this.initializeOptionSelectionState());
        setTimeout(() => this.initializeOptionSelectionState(), 0);
        this.addEventListener('change', this.handleProductOptionSelection.bind(this));

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        // Cache the first slide's original image src/srcset/sizes once on load.
        // Used to restore the main image when user clicks a thumbnail after a swatch.
        this.cacheOriginalGalleryImage();

        // Swatch click → rewrite first slide to variant image.
        this.addEventListener('change', this.handleSwatchInstantSwap.bind(this));
        this.addEventListener('click', this.handleSwatchInstantSwap.bind(this));

        // Show selected color name on page load if any color is already selected.
        this.updateSelectedColorName();

        // Thumbnail click → restore first slide to its ORIGINAL src.
        // Native media-gallery.js sets the clicked thumb's <li> active.
        // If user clicked the FIRST thumb, clear any swatch-image rewrite.
        this.addEventListener('click', this.handleThumbnailRestore.bind(this));

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disableAddToCartUntilSelection() {
        this.initializeOptionSelectionState();
      }

      initializeOptionSelectionState() {
        this.optionSelections.clear();
        this.clearOptionSelections();
        this.updateSelectedColorName();
        this.updateAddToCartState();
      }

      handleProductOptionSelection(event) {
        if (!event.target.closest('variant-selects')) return;

        const target =
          event.target.tagName === 'SELECT' && event.target.selectedOptions.length
            ? event.target.selectedOptions[0]
            : event.target;

        this.rememberOptionSelection(target);
        this.restoreOptionSelections();
        this.updateSelectedColorName();
        this.updateAddToCartState();
      }

      getOptionGroups(variantSelects = this.variantSelectors) {
        if (!variantSelects) return [];

        const fieldsetGroups = Array.from(variantSelects.querySelectorAll('fieldset')).map((fieldset) => {
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          const legend = fieldset.querySelector('legend.form__label');

          return {
            type: 'radio',
            key: inputs[0]?.name,
            label: this.getCleanOptionLabel(legend),
            element: fieldset,
            required: inputs.some((input) => input.required) || inputs.length > 0,
          };
        });

        const selectGroups = Array.from(variantSelects.querySelectorAll('select')).map((select) => {
          const label = variantSelects.querySelector(`label[for="${CSS.escape(select.id)}"]`);

          return {
            type: 'select',
            key: select.name || select.id,
            label: this.getCleanOptionLabel(label),
            element: select,
            required: select.required || true,
          };
        });

        return [...fieldsetGroups, ...selectGroups].filter((group) => group.key);
      }

      getCleanOptionLabel(labelElement) {
        if (!labelElement) return '';

        const clone = labelElement.cloneNode(true);
        clone.querySelectorAll('[data-selected-value], .selected-color-name').forEach((element) => element.remove());

        return clone.textContent.replace('*', '').replace(':', '').trim();
      }

      getOptionGroupKeyForTarget(target) {
        if (!target) return null;

        if (target.tagName === 'OPTION') {
          const select = target.closest('select');
          return select?.name || select?.id || null;
        }

        if (target.matches?.('input[type="radio"]')) return target.name;

        return null;
      }

      rememberOptionSelection(target) {
        const key = this.getOptionGroupKeyForTarget(target);
        if (!key) return;

        this.optionSelections.set(key, {
          id: target.dataset.optionValueId || '',
          value: target.value || '',
          productUrl: target.dataset.productUrl || '',
        });
      }

      syncOptionSelectionsFromDom(variantSelects = this.variantSelectors) {
        this.getOptionGroups(variantSelects).forEach((group) => {
          const selected =
            group.type === 'radio'
              ? group.element.querySelector('input[type="radio"]:checked')
              : group.element.selectedOptions[0];

          if (selected) this.rememberOptionSelection(selected);
        });
      }

      clearOptionSelections(variantSelects = this.variantSelectors) {
        this.getOptionGroups(variantSelects).forEach((group) => {
          if (group.type === 'radio') {
            group.element.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.checked = false;
              input.removeAttribute('checked');
            });
            return;
          }

          Array.from(group.element.options).forEach((option) => option.removeAttribute('selected'));
        });
      }

      restoreOptionSelections(variantSelects = this.variantSelectors) {
        this.getOptionGroups(variantSelects).forEach((group) => {
          const selection = this.optionSelections.get(group.key);

          if (group.type === 'radio') {
            group.element.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.checked =
                !!selection &&
                ((!!selection.id && input.dataset.optionValueId === selection.id) || input.value === selection.value);
            });
            return;
          }

          Array.from(group.element.options).forEach((option) => {
            const isSelected =
              !!selection &&
              ((!!selection.id && option.dataset.optionValueId === selection.id) || option.value === selection.value);
            option.selected = isSelected;
            option.toggleAttribute('selected', isSelected);
          });
        });
      }

      areAllRequiredOptionsSelected(variantSelects = this.variantSelectors) {
        const requiredGroups = this.getOptionGroups(variantSelects).filter((group) => group.required);
        if (!requiredGroups.length) return true;

        return requiredGroups.every((group) => {
          const selection = this.optionSelections.get(group.key);
          return !!selection?.value;
        });
      }

      getSelectedOptionValueIds() {
        return this.getOptionGroups()
          .map((group) => this.optionSelections.get(group.key)?.id)
          .filter(Boolean);
      }

      setProductFormVariantId(variantId = '') {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;

          input.value = variantId || '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      setSubmitButtonState(disabled, text) {
        const submitButton = this.querySelector('[id^="ProductSubmitButton"], [type="submit"], button[name="add"]');
        if (!submitButton) return;

        const submitButtonText = submitButton.querySelector('span');

        submitButton.toggleAttribute('disabled', disabled);
        submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (submitButtonText && text) {
          submitButtonText.textContent = text;
        } else if (submitButtonText && !disabled) {
          submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      updateAddToCartState(variant = undefined, submitButtonDisabled = undefined) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return;

        const allOptionsSelected = this.areAllRequiredOptionsSelected(variantSelects);
        const selectOptionText = window.variantStrings?.selectOption || 'Select an option';

        if (!allOptionsSelected) {
          this.setProductFormVariantId('');
          this.setSubmitButtonState(true, selectOptionText);
          return;
        }

        if (variant === undefined) return;

        if (!variant || submitButtonDisabled) {
          this.setProductFormVariantId('');
          this.setSubmitButtonState(true, window.variantStrings?.soldOut || 'Sold out');
          return;
        }

        this.setProductFormVariantId(variant.id ?? '');
        this.setSubmitButtonState(!variant.available, variant.available ? undefined : window.variantStrings?.soldOut);
      }

      cacheOriginalGalleryImage() {
        const galleryViewer = this.querySelector('[id^="GalleryViewer"]');
        if (!galleryViewer) return;

        const firstSlide = galleryViewer.querySelector('li[data-media-id]');
        if (!firstSlide) return;

        const img = firstSlide.querySelector('img');
        if (!img) return;

        if (!img.dataset.originalSrc) {
          img.dataset.originalSrc = img.getAttribute('src') || '';
          img.dataset.originalSrcset = img.getAttribute('srcset') || '';
          img.dataset.originalSizes = img.getAttribute('sizes') || '';
          img.dataset.originalAlt = img.getAttribute('alt') || '';
        }
      }

      // Stamp the currently-checked value next to the color option legend.
      // Runs both after a click (instant feedback) and after the variant-selects HTML is
      // re-rendered, so previously-chosen options keep their label suffix.
      updateSelectedColorName(targetInput = null) {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset) => {
          const legend = fieldset.querySelector('legend.form__label');
          if (!legend) return;

          const optionName = (legend.childNodes[0]?.textContent || '').replace('*', '').replace(':', '').trim();
          if (!/^colou?r$/i.test(optionName)) {
            legend.querySelector('.selected-color-name')?.remove();
            return;
          }

          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          let selectedText = legend.querySelector('[data-selected-value]') || legend.querySelector('.selected-color-name');

          if (!checkedInput) {
            if (selectedText?.hasAttribute('data-selected-value')) {
              selectedText.textContent = '';
            } else if (selectedText) {
              selectedText.remove();
            }
            return;
          }

          let valueLabel = checkedInput.value || '';
          if (!valueLabel && checkedInput.id) {
            const lbl = fieldset.querySelector(`label[for="${CSS.escape(checkedInput.id)}"]`);
            valueLabel = lbl?.getAttribute('title') || lbl?.getAttribute('aria-label') || '';
          }
          if (!valueLabel) return;

          if (!selectedText) {
            selectedText = document.createElement('span');
            selectedText.className = 'selected-color-name';
            legend.appendChild(selectedText);
          }

          selectedText.textContent = selectedText.hasAttribute('data-selected-value') ? valueLabel : `: ${valueLabel}`;
        });
      }

      handleSwatchInstantSwap(event) {
        const target = event.target;
        if (!target) return;

        const input = target.matches?.('input.variant-image-swatch__input')
          ? target
          : target.closest?.('label')?.previousElementSibling;

        if (input && input.matches?.('input.variant-image-swatch__input')) {
          this.updateSelectedColorName(input);
        }

        const src = target.dataset?.variantImage || input?.dataset?.variantImage;
        if (!src) return;

        // Swatch click controls main image. Rewrite first slide and force it active.
        this.swapMainImageBySrc(src);
      }

      handleThumbnailRestore(event) {
        // Detect any click inside a gallery thumbnail item.
        const thumb = event.target.closest && event.target.closest('.thumbnail-list__item');
        if (!thumb) return;

        // Thumbnail click controls main image. Restore first slide's original src
        // so when first thumb's <li> is shown active, it displays the original image, not the swatch.
        this.restoreOriginalFirstSlide();
      }

      restoreOriginalFirstSlide() {
        const galleryViewer = this.querySelector('[id^="GalleryViewer"]');
        if (!galleryViewer) return;

        const firstSlide = galleryViewer.querySelector('li[data-media-id]');
        if (!firstSlide) return;

        const img = firstSlide.querySelector('img');
        if (!img) return;

        const origSrc = img.dataset.originalSrc;
        if (!origSrc) return;

        img.setAttribute('src', origSrc);

        if (img.dataset.originalSrcset) {
          img.setAttribute('srcset', img.dataset.originalSrcset);
        } else {
          img.removeAttribute('srcset');
        }

        if (img.dataset.originalSizes) {
          img.setAttribute('sizes', img.dataset.originalSizes);
        } else {
          img.removeAttribute('sizes');
        }

        if (img.dataset.originalAlt) {
          img.setAttribute('alt', img.dataset.originalAlt);
        }
      }

      swapMainImageBySrc(src) {
        if (!src) return;

        const fullSrc = src.startsWith('//') ? 'https:' + src : src;
        const galleryViewer = this.querySelector('[id^="GalleryViewer"]');
        if (!galleryViewer) return;

        const slides = galleryViewer.querySelectorAll('li[data-media-id]');
        if (!slides.length) return;

        const firstSlide = slides[0];
        const firstMediaId = firstSlide.dataset.mediaId;
        const firstSlideImg = firstSlide.querySelector('img');
        if (!firstSlideImg) return;

        // Ensure original is cached BEFORE we overwrite.
        if (!firstSlideImg.dataset.originalSrc) {
          firstSlideImg.dataset.originalSrc = firstSlideImg.getAttribute('src') || '';
          firstSlideImg.dataset.originalSrcset = firstSlideImg.getAttribute('srcset') || '';
          firstSlideImg.dataset.originalSizes = firstSlideImg.getAttribute('sizes') || '';
          firstSlideImg.dataset.originalAlt = firstSlideImg.getAttribute('alt') || '';
        }

        // Rewrite first slide image to swatch/variant image.
        firstSlideImg.removeAttribute('srcset');
        firstSlideImg.removeAttribute('sizes');
        firstSlideImg.src = fullSrc;

        const opener = firstSlide.querySelector('.product__modal-opener, a');
        if (opener && 'href' in opener) {
          try {
            opener.href = fullSrc;
          } catch (e) {}
        }

        // Force first slide active via media-gallery API.
        const mediaGallery = this.querySelector('media-gallery');

        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function' && firstMediaId) {
          try {
            mediaGallery.setActiveMedia(firstMediaId, false);
          } catch (e) {}
        } else {
          slides.forEach((s) => s.classList.remove('is-active'));
          firstSlide.classList.add('is-active');

          this.querySelectorAll('.thumbnail-list__item .thumbnail').forEach((b) => {
            b.removeAttribute('aria-current');
          });

          const matchingThumb = this.querySelector(`.thumbnail-list__item[data-target="${firstMediaId}"] .thumbnail`);
          if (matchingThumb) matchingThumb.setAttribute('aria-current', 'true');
        }

        const viewerSlider = galleryViewer.slider || galleryViewer.querySelector('ul');
        if (viewerSlider) viewerSlider.scrollLeft = 0;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target } }) {
        if (!this.contains(event.target)) return;

        this.rememberOptionSelection(target);
        const selectedOptionValueIds = this.getSelectedOptionValueIds();

        this.resetProductFormState();
        this.updateAddToCartState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValueIds, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            requestAnimationFrame(() => this.updateSelectedColorName());
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            const t = document.querySelector(`#${CSS.escape(targetId)}`);

            if (t) {
              if (t.type === 'radio') t.checked = true;
              t.focus();

              this.restoreOptionSelections();
              this.updateSelectedColorName(t);
              this.updateAddToCartState();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');

        if (variantSelects) {
          this.restoreOptionSelections(variantSelects);
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          requestAnimationFrame(() => {
            this.restoreOptionSelections();
            this.updateSelectedColorName();
            this.updateAddToCartState();
          });
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const submitButtonDisabled =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);

          // Re-apply selected color name after option HTML is refreshed.
          requestAnimationFrame(() => {
            this.restoreOptionSelections();
            this.updateSelectedColorName();
            this.updateAddToCartState(variant, submitButtonDisabled);
          });

          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, submitButtonDisabled);

          if (!this.areAllRequiredOptionsSelected()) {
            this.updateAddToCartState();
            return;
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.swapMainImageForVariant(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.updateAddToCartState(variant, submitButtonDisabled);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId, submitButtonDisabled = false) {
        const safeVariantId = this.areAllRequiredOptionsSelected() && !submitButtonDisabled ? variantId : null;
        this.setProductFormVariantId(safeVariantId ?? '');
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');

        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      swapMainImageForVariant(variant) {
        const src = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
        if (!src) return;

        this.swapMainImageBySrc(src);

        const galleryViewer = this.querySelector('[id^="GalleryViewer"]');
        if (!galleryViewer) return;

        const firstSlideImg = galleryViewer.querySelector('li[data-media-id].is-active img');

        if (firstSlideImg) {
          firstSlideImg.alt = variant?.featured_image?.alt || variant?.featured_media?.alt || firstSlideImg.alt || '';
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));

          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );

          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );

          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);

        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);

              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;

            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');

              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);

                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);

                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );

        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );

        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
